import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Globe, LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, signOut } = useAuth();
  const { t, i18n } = useTranslation();
  
  const language = i18n.language.toUpperCase();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setIsOpen(false);
  };

  const navLinks = [
    { path: "/", label: t("nav.home") },
    { path: "/portfolio", label: t("nav.portfolio") },
    { path: "/services", label: t("nav.services") },
    { path: "/about", label: t("nav.about") },
    { path: "/faq", label: t("nav.faq") },
    { path: "/contact", label: t("nav.contact") },
  ];

  const languages = [
    { code: "AF", label: "🇳🇦🇿🇦 AF" },
    { code: "DE", label: "🇩🇪 DE" },
    { code: "EL", label: "🇬🇷 EL" },
    { code: "EN", label: "🇬🇧 EN" },
    { code: "ES", label: "🇪🇸 ES" },
    { code: "FR", label: "🇫🇷 FR" },
    { code: "IT", label: "🇮🇹 IT" },
    { code: "NL", label: "🇳🇱 NL" },
    { code: "PT", label: "🇵🇹 PT" },
  ];
  
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang.toLowerCase());
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "bg-background/95 backdrop-blur-md shadow-lg" : "bg-background/80 backdrop-blur-sm"
      }`}
      role="navigation"
      aria-label="Main navigation"
    >
      <div className="container-page py-4 md:py-5">
        <div className="flex items-center justify-between relative">
          <Link 
            to="/" 
            className="relative font-heading font-light text-primary hover:text-gold-dark transition-colors z-10 flex items-center min-w-0 flex-1 mr-4"
            aria-label={t("navigation.ariaLabel")}
          >
            <span className="font-semibold tracking-[0.02em] leading-none whitespace-nowrap text-ellipsis overflow-hidden" style={{ fontSize: 'clamp(1.25rem, 3vw + 0.5rem, 1.75rem)' }}>
              <span className="font-light">LEUCHTENDE</span>
              <span className="font-bold text-foreground">HOTEL</span>
              <span className="font-light">FOTOGRAFIE</span>
            </span>
          </Link>

          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                className="text-primary hover:text-gold-dark p-2 z-10 flex-shrink-0" 
                size="icon"
                aria-label={t("navigation.openMenu")}
                aria-expanded={isOpen}
              >
                <Menu className="h-10 w-10 md:h-12 md:w-12" aria-hidden="true" />
                <span className="sr-only">{t("navigation.openMenu")}</span>
              </Button>
            </SheetTrigger>
            <SheetContent 
              side="right" 
              className="w-[300px] sm:w-[400px]"
              onEscapeKeyDown={() => setIsOpen(false)}
            >
              <SheetHeader>
                <SheetTitle className="text-left font-heading text-primary">
                  {t("navigation.menu")}
                </SheetTitle>
              </SheetHeader>
              <nav className="flex flex-col gap-6 mt-8" aria-label="Primary navigation">
                {navLinks.map((link) => (
                  link.path === "/portfolio" ? (
                    <button
                      key={link.path}
                      onClick={() => {
                        setIsOpen(false);
                        navigate("/portfolio");
                      }}
                      className={`font-body text-lg tracking-wide transition-colors text-left ${
                        location.pathname === link.path ? "text-primary font-medium" : "text-foreground hover:text-primary"
                      }`}
                      aria-current={location.pathname === link.path ? "page" : undefined}
                    >
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsOpen(false)}
                      className={`font-body text-lg tracking-wide transition-colors ${
                        location.pathname === link.path ? "text-primary font-medium" : "text-foreground hover:text-primary"
                      }`}
                      aria-current={location.pathname === link.path ? "page" : undefined}
                    >
                      {link.label}
                    </Link>
                  )
                ))}
                
                {/* Admin-only Insights link */}
                {isAdmin && (
                  <Link
                    to="/insights"
                    onClick={() => setIsOpen(false)}
                    className={`font-body text-lg tracking-wide transition-colors ${
                      location.pathname === "/insights" ? "text-primary font-medium" : "text-foreground hover:text-primary"
                    }`}
                    aria-current={location.pathname === "/insights" ? "page" : undefined}
                  >
                    Insights
                  </Link>
                )}
                
                {/* Admin-only PhotoSync link */}
                {isAdmin && (
                  <Link
                    to="/admin"
                    onClick={() => setIsOpen(false)}
                    className={`font-body text-lg tracking-wide transition-colors ${
                      location.pathname === "/admin" ? "text-primary font-medium" : "text-foreground hover:text-primary"
                    }`}
                    aria-current={location.pathname === "/admin" ? "page" : undefined}
                  >
                    PhotoSync
                  </Link>
                )}
                
                <div className="border-t border-border pt-6 mt-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <Globe className="w-5 h-5 text-muted-foreground" aria-hidden="true" />
                    <select
                      value={language}
                      onChange={(e) => handleLanguageChange(e.target.value)}
                      className="bg-background text-foreground border border-border rounded px-3 py-2 outline-none cursor-pointer font-body flex-1"
                      aria-label={t("navigation.selectLanguage")}
                    >
                      {languages.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {user && (
                    <Button
                      variant="outline"
                      className="w-full gap-2"
                      onClick={handleSignOut}
                      aria-label="Abmelden"
                    >
                      <LogOut className="w-4 h-4" aria-hidden="true" />
                      {t("nav.signOut")}
                    </Button>
                  )}
                </div>
              </nav>
            </SheetContent>
          </Sheet>
          
          {/* Continuous shimmer effect spanning full width */}
          <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer pointer-events-none"></span>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
